import { useForm, usePage } from '@inertiajs/react'
import { Info } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { t } from '@/shared/lib/i18n'
import {
  adminInstrumentTypeControlsReagentsPath,
  adminInstrumentTypeControlsReagentPath,
  lotAdminInstrumentTypeControlsReagentsPath,
} from '@/shared/lib/routes'
import {
  kitMissingSecondary,
  kitSecondaryBlocksSubmit,
  secondaryNameMissing,
} from '@/pages/Admin/InstrumentTypes/controls-reagents-kit-gates'

export interface ControlsReagentsRow {
  lotId: number
  name: string | null
  instrumentTaskId: number | null
  requiresValidation: boolean
  isKit: boolean
  notes: string | null
  secondaryLots: { id: number; name: string | null; description: string | null }[]
}

export interface CatalogLotOption {
  id: number
  label: string
}

interface SecondaryRow {
  id?: number
  name: string
  description: string
}

interface ControlsFormData {
  name: string
  requiresValidation: boolean
  isKit: boolean
  notes: string
  secondaryLots: SecondaryRow[]
  existingLotId: string
}

const NO_EXISTING_LOT_VALUE = '__none__'

const buildInitial = (editingRow: ControlsReagentsRow | null): ControlsFormData => {
  if (!editingRow) {
    return {
      name: '',
      requiresValidation: false,
      isKit: false,
      notes: '',
      secondaryLots: [],
      existingLotId: '',
    }
  }

  return {
    name: editingRow.name ?? '',
    requiresValidation: editingRow.requiresValidation,
    isKit: editingRow.isKit,
    notes: editingRow.notes ?? '',
    secondaryLots: editingRow.secondaryLots.map((secondary) => ({
      id: secondary.id,
      name: secondary.name ?? '',
      description: secondary.description ?? '',
    })),
    existingLotId: '',
  }
}

export type ControlsReagentsPersistPayload = {
  lot: {
    name: string
    requires_validation: boolean
    is_kit: boolean
    notes: string
    secondary_lots: { id?: number; name: string; description: string }[]
  }
  lot_id?: string
  /** Draft-create only: temporary id maintenance tasks use to reference this unsaved supply. */
  client_lot_id?: string
}

const ControlsReagentsModalForm = ({
  typeId,
  editingRow,
  selectableCatalogLots,
  onClose,
  draftMode = false,
  onLocalSave,
}: {
  typeId: number | null
  editingRow: ControlsReagentsRow | null
  selectableCatalogLots: CatalogLotOption[]
  onClose: () => void
  draftMode?: boolean
  onLocalSave?: (payload: ControlsReagentsPersistPayload, displayRow: ControlsReagentsRow) => void
}) => {
  const isEdit = editingRow !== null
  const form = useForm<ControlsFormData>(buildInitial(editingRow))
  const { errors: pageErrors } = usePage<{ errors?: Record<string, string[] | undefined> }>().props
  const baseErrors = pageErrors?.base ?? []
  const nameErrors = pageErrors?.name ?? []

  const setSecondaryRow = (rowIndex: number, patch: Partial<SecondaryRow>) => {
    form.setData(
      'secondaryLots',
      form.data.secondaryLots.map((row, index) =>
        index === rowIndex ? { ...row, ...patch } : row,
      ),
    )
  }

  const addSecondary = () => {
    form.setData('secondaryLots', [...form.data.secondaryLots, { name: '', description: '' }])
  }

  const removeSecondary = (rowIndex: number) => {
    form.setData(
      'secondaryLots',
      form.data.secondaryLots.filter((_, index) => index !== rowIndex),
    )
  }

  const onPickExistingLot = (value: string) => {
    if (value === NO_EXISTING_LOT_VALUE) {
      form.setData('existingLotId', '')
      return
    }

    const picked = selectableCatalogLots.find((lot) => String(lot.id) === value)
    form.setData((previous) => ({
      ...previous,
      existingLotId: value,
      name: previous.name.trim().length > 0 ? previous.name : (picked?.label ?? previous.name),
    }))
  }

  const submit = () => {
    const draftLotId = editingRow?.lotId ?? -Date.now()
    const persistPayload: ControlsReagentsPersistPayload = {
      lot: {
        name: form.data.name.trim(),
        requires_validation: form.data.requiresValidation,
        is_kit: form.data.isKit,
        notes: form.data.notes,
        secondary_lots: form.data.isKit
          ? form.data.secondaryLots.map((row) => ({
              id: row.id,
              name: row.name.trim(),
              description: row.description.trim(),
            }))
          : [],
      },
      ...(isEdit ? {} : { lot_id: form.data.existingLotId || '' }),
      ...(draftMode ? { client_lot_id: String(draftLotId) } : {}),
    }

    if (draftMode && onLocalSave) {
      const displayRow: ControlsReagentsRow = {
        lotId: draftLotId,
        name: persistPayload.lot.name,
        instrumentTaskId: editingRow?.instrumentTaskId ?? null,
        requiresValidation: persistPayload.lot.requires_validation,
        isKit: persistPayload.lot.is_kit,
        notes: persistPayload.lot.notes || null,
        secondaryLots: persistPayload.lot.secondary_lots.map((secondary, index) => ({
          id: secondary.id ?? -(index + 1),
          name: secondary.name || null,
          description: secondary.description || null,
        })),
      }
      onLocalSave(persistPayload, displayRow)
      form.reset()
      onClose()
      return
    }

    if (typeId === null) return

    form.transform(() => persistPayload)

    const onSuccess = () => {
      form.reset()
      onClose()
    }

    if (isEdit) {
      if (editingRow.instrumentTaskId !== null) {
        form.patch(adminInstrumentTypeControlsReagentPath(typeId, editingRow.instrumentTaskId), {
          preserveScroll: true,
          onSuccess,
        })
      } else {
        // Linked-only lot: no backing requirement task, edit the lot in place by id.
        form.patch(lotAdminInstrumentTypeControlsReagentsPath(typeId, editingRow.lotId), {
          preserveScroll: true,
          onSuccess,
        })
      }
    } else {
      form.post(adminInstrumentTypeControlsReagentsPath(typeId), {
        preserveScroll: true,
        onSuccess,
      })
    }
  }

  const kitSecondaryInvalid = kitSecondaryBlocksSubmit({
    isKit: form.data.isKit,
    secondaryLots: form.data.secondaryLots,
  })
  const showKitRequiresSecondary = kitMissingSecondary({
    isKit: form.data.isKit,
    secondaryLots: form.data.secondaryLots,
  })
  const submitDisabled =
    form.processing || form.data.name.trim().length === 0 || kitSecondaryInvalid

  return (
    <DialogContent className="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>
          {isEdit
            ? t('admin.instrumentTypes.controlsModal.editTitle')
            : t('admin.instrumentTypes.controlsModal.addTitle')}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1 -m-1">
        {baseErrors.length > 0 && (
          <div className="rounded border border-destructive bg-destructive/10 px-3 py-2">
            {baseErrors.map((message, index) => (
              <p key={index} className="text-sm text-destructive">
                {message}
              </p>
            ))}
          </div>
        )}

        {!isEdit && selectableCatalogLots.length > 0 && (
          <div className="space-y-1.5">
            <Label>{t('admin.instrumentTypes.controlsModal.useExistingLabel')}</Label>
            <Select
              value={
                form.data.existingLotId === '' ? NO_EXISTING_LOT_VALUE : form.data.existingLotId
              }
              onValueChange={onPickExistingLot}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t('admin.instrumentTypes.controlsModal.useExistingPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_EXISTING_LOT_VALUE}>
                  {t('admin.instrumentTypes.controlsModal.useExistingNone')}
                </SelectItem>
                {selectableCatalogLots.map((lot) => (
                  <SelectItem key={lot.id} value={String(lot.id)}>
                    {lot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="controls-modal-name">
            {t('admin.instrumentTypes.controlsModal.nameLabel')} *
          </Label>
          <Input
            id="controls-modal-name"
            value={form.data.name}
            onChange={(event) => form.setData('name', event.target.value)}
            placeholder={t('admin.instrumentTypes.controlsModal.namePlaceholder')}
            autoComplete="off"
          />
          {nameErrors.map((message, index) => (
            <p key={index} className="text-sm text-destructive">
              {message}
            </p>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Checkbox
              id="controls-modal-requires-validation"
              checked={form.data.requiresValidation}
              onCheckedChange={(checked) => form.setData('requiresValidation', checked === true)}
            />
            <Label htmlFor="controls-modal-requires-validation" className="font-normal">
              {t('admin.instrumentTypes.controlsModal.requiresValidation')}
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="controls-modal-is-kit"
              checked={form.data.isKit}
              onCheckedChange={(checked) => form.setData('isKit', checked === true)}
            />
            <Label htmlFor="controls-modal-is-kit" className="font-normal">
              {t('admin.instrumentTypes.controlsModal.isKit')}
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t('admin.instrumentTypes.controlsModal.isKitInfoLabel')}
                >
                  <Info className="h-4 w-4" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t('admin.instrumentTypes.controlsModal.isKitTooltip')}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {form.data.isKit ? (
          <div className="space-y-3 rounded-md border p-3">
            <h3 className="text-sm font-semibold">
              {t('admin.instrumentTypes.controlsModal.secondariesTitle')}
            </h3>
            {showKitRequiresSecondary && (
              <p className="text-sm text-destructive" role="alert">
                {t('admin.instrumentTypes.controlsModal.kitRequiresSecondary')}
              </p>
            )}
            {form.data.secondaryLots.map((row, rowIndex) => {
              const nameRequiredMissing = secondaryNameMissing({
                isKit: form.data.isKit,
                name: row.name,
              })
              const secondaryNameInputId = `controls-modal-secondary-name-${rowIndex}`
              const secondaryNameErrorId = `controls-modal-secondary-name-error-${rowIndex}`
              const secondaryDescriptionInputId = `controls-modal-secondary-description-${rowIndex}`
              return (
                <div key={row.id ?? `new-${rowIndex}`} className="space-y-2 rounded-md border p-3">
                  <div className="space-y-1">
                    <Label htmlFor={secondaryNameInputId}>
                      {t('admin.instrumentTypes.controlsModal.secondaryName')} *
                    </Label>
                    <Input
                      id={secondaryNameInputId}
                      value={row.name}
                      onChange={(event) => setSecondaryRow(rowIndex, { name: event.target.value })}
                      placeholder={t(
                        'admin.instrumentTypes.controlsModal.secondaryNamePlaceholder',
                      )}
                      autoComplete="off"
                      aria-required
                      aria-invalid={nameRequiredMissing}
                      aria-describedby={nameRequiredMissing ? secondaryNameErrorId : undefined}
                    />
                    {nameRequiredMissing && (
                      <p
                        id={secondaryNameErrorId}
                        className="text-sm text-destructive"
                        role="alert"
                      >
                        {t('admin.instrumentTypes.controlsModal.secondaryNameRequired')}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={secondaryDescriptionInputId}>
                      {t('admin.instrumentTypes.controlsModal.secondaryDescription')}
                    </Label>
                    <Input
                      id={secondaryDescriptionInputId}
                      value={row.description}
                      onChange={(event) =>
                        setSecondaryRow(rowIndex, { description: event.target.value })
                      }
                      placeholder={t(
                        'admin.instrumentTypes.controlsModal.secondaryDescriptionPlaceholder',
                      )}
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => removeSecondary(rowIndex)}
                  >
                    {t('admin.instrumentTypes.controlsModal.secondaryDelete')}
                  </Button>
                </div>
              )
            })}
            <Button type="button" variant="outline" size="sm" onClick={addSecondary}>
              {t('admin.instrumentTypes.controlsModal.addSecondary')}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('admin.instrumentTypes.controlsModal.secondariesHiddenNote')}
          </p>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="controls-modal-notes">
            {t('admin.instrumentTypes.controlsModal.notes')}
          </Label>
          <RichTextEditor
            id="controls-modal-notes"
            value={form.data.notes}
            onChange={(htmlValue) => form.setData('notes', htmlValue)}
            placeholder={t('admin.instrumentTypes.controlsModal.notesPlaceholder')}
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('admin.instrumentTypes.controlsModal.cancel')}
        </Button>
        <Button type="button" onClick={submit} disabled={submitDisabled}>
          {t('admin.instrumentTypes.controlsModal.save')}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

export const ControlsReagentsModal = ({
  open,
  onOpenChange,
  typeId,
  editingRow,
  selectableCatalogLots,
  draftMode = false,
  onLocalSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  typeId: number | null
  editingRow: ControlsReagentsRow | null
  selectableCatalogLots: CatalogLotOption[]
  draftMode?: boolean
  onLocalSave?: (payload: ControlsReagentsPersistPayload, displayRow: ControlsReagentsRow) => void
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    {open && (
      <ControlsReagentsModalForm
        // Remount the form whenever the target row changes so useForm re-initializes.
        key={editingRow ? `edit-${editingRow.lotId}` : 'new'}
        typeId={typeId}
        editingRow={
          editingRow
            ? {
                lotId: editingRow.lotId,
                name: editingRow.name,
                instrumentTaskId: editingRow.instrumentTaskId,
                requiresValidation: editingRow.requiresValidation,
                isKit: editingRow.isKit,
                notes: editingRow.notes,
                secondaryLots: editingRow.secondaryLots,
              }
            : null
        }
        selectableCatalogLots={selectableCatalogLots}
        onClose={() => onOpenChange(false)}
        draftMode={draftMode}
        onLocalSave={onLocalSave}
      />
    )}
  </Dialog>
)

export default ControlsReagentsModal
